import { useMemo, useState } from 'react';
import { alpha, Box, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { BasicButton } from '../../components/atoms';
import { Accordion, AccordionItemData } from '../../components/molecules';
import { useAppSelector } from '../../common/hooks';
import { useCMSContent } from '../../common/hooks/useCmsContent';

const getFaqKeyPrefix = (pageName: string) => `resources.${pageName}.faq`;

const FaqWrapper = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    width: '100%'
}));

const SupportPanel = styled(Box)(({ theme }) => ({
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(3),
    width: '100%',
    padding: theme.spacing(2, 4),
    borderRadius: '6px',
    backgroundImage: `linear-gradient(99.73deg, ${alpha(theme.palette.semantic.int.selected, 0.03)} 7.36%, ${alpha(
        theme.palette.semantic.int.selected,
        0.15
    )} 175.72%)`
}));

const SupportHeading = styled('p')(({ theme }) => ({
    margin: 0,
    fontFamily: '"TT Commons Pro"',
    fontSize: '20px',
    fontWeight: 700,
    lineHeight: '26px',
    color: theme.palette.primary[800]
}));

export const FaqSection = ({ pageName }: { pageName: string }) => {
    const { t } = useTranslation();
    const supportEmail = useAppSelector(
        (state) => state.global?.globalConfig?.supportEmail || t('APPLICATION_SUPPORT_EMAIL')
    );
    const cmsData = useAppSelector((state) => state.global?.cmsData);
    const cmsUtil = useCMSContent();

    const faqItems = useMemo<AccordionItemData[]>(() => {
        const items: AccordionItemData[] = [];
        if (!cmsData) return items;
        const prefix = getFaqKeyPrefix(pageName);
        for (let i = 1; cmsData[`${prefix}${i}.question`]; i++) {
            const answerKey = `${prefix}${i}.answer`;
            items.push({
                id: `${pageName}-faq${i}`,
                title: cmsUtil.getContentAsString(`${prefix}${i}.question`),
                content: cmsData[answerKey] ? cmsUtil.getContentAsElement(answerKey) : ''
            });
        }
        return items;
    }, [cmsData, pageName, t]);

    const [expandedIds, setExpandedIds] = useState<string[] | null>(null);

    return (
        <FaqWrapper>
            <Accordion
                items={faqItems}
                expandedIds={expandedIds ?? (faqItems[0] ? [faqItems[0].id] : [])}
                onChange={setExpandedIds}
            />
            <SupportPanel>
                <SupportHeading>{t('RESOURCES_FAQ_SUPPORT_HEADING')}</SupportHeading>
                <BasicButton variant="contained" href={`mailto:${supportEmail}`}>
                    {t('RESOURCES_FAQ_CONTACT_SUPPORT')}
                </BasicButton>
            </SupportPanel>
        </FaqWrapper>
    );
};
